/**
 * THE ONLY PLACE IN THIS APP ALLOWED TO CONSTRUCT Intl.NumberFormat OR
 * Intl.DateTimeFormat. scripts/check-hardcoded.mjs fails the build on any other
 * use, because a single stray `new Intl.NumberFormat(navigator.language)` is all
 * it takes to print Arabic-Indic digits somewhere and break the hard invariant.
 *
 * The invariant: every rendered digit is 0-9. Never Arabic-Indic (U+0660–U+0669)
 * and never Extended Arabic-Indic (U+06F0–U+06F9). Separators stay Latin — "."
 * for decimals and "," for thousands — in Arabic too. This is standard practice
 * in Gulf financial interfaces.
 *
 * The Arabic UI keeps Arabic script and Arabic month names. ONLY digits and
 * numeric separators are forced Latin.
 */

export type AppLocale = "en" | "ar";

/**
 * BCP-47 tags, forcing BOTH the Latin numbering system and the Gregorian
 * calendar in the tag itself.
 *
 * `-ca-gregory` IS LOAD-BEARING — do not remove it.
 * CLDR's default calendar for `ar-SA` is islamic-umalqura, so a plain
 * `ar-SA` renders "٢٦ صفر ١٤٤٨ هـ" instead of "09 أغسطس 2026". This shipped:
 * the app showed a Hijri date as the primary date on a real phone, while this
 * development machine's ICU happened to default to gregory and hid it
 * completely. The lesson is the same one the numbering system taught — never
 * rely on an implementation's default, force it in the tag AND in the options,
 * and assert the RESOLVED value rather than the formatted string.
 *
 * The product brief makes Gregorian the source of truth and the primary
 * display. A Hijri secondary line may be added later; it would be an explicit,
 * separately formatted string, never a locale default.
 *
 * en-GB rather than en-US deliberately: it yields day-month-year ("07 Aug
 * 2026"), the order used across the design and unambiguous to every reader,
 * where "08/07/2026" is not. Number formatting is identical between the two.
 */
const LOCALE_TAG: Readonly<Record<AppLocale, string>> = {
  en: "en-GB-u-nu-latn-ca-gregory",
  ar: "ar-SA-u-nu-latn-ca-gregory",
};

const ARABIC_INDIC_ZERO = 0x0660;
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0;
const ARABIC_DECIMAL_SEPARATOR = 0x066b;
const ARABIC_THOUSANDS_SEPARATOR = 0x066c;
const ARABIC_LETTER_MARK = 0x061c;
const LTR_MARK = 0x200e;
const RTL_MARK = 0x200f;
const NBSP = 0x00a0;
const NARROW_NBSP = 0x202f;

/**
 * Belt and braces. Requesting `-u-nu-latn` AND `numberingSystem: "latn"` should
 * be enough, but ICU versions differ in whether they also localise the
 * SEPARATORS for Arabic locales. Rather than trust that, every formatted string
 * is rewritten through here. It is cheap, and it makes the invariant true by
 * construction rather than by hope.
 */
export function latiniseDigits(input: string): string {
  let out = "";
  for (const char of input) {
    const code = char.codePointAt(0) ?? 0;

    if (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) {
      out += String.fromCharCode(0x30 + (code - ARABIC_INDIC_ZERO));
    } else if (
      code >= EXTENDED_ARABIC_INDIC_ZERO &&
      code <= EXTENDED_ARABIC_INDIC_ZERO + 9
    ) {
      out += String.fromCharCode(0x30 + (code - EXTENDED_ARABIC_INDIC_ZERO));
    } else if (code === ARABIC_DECIMAL_SEPARATOR) {
      out += ".";
    } else if (code === ARABIC_THOUSANDS_SEPARATOR) {
      out += ",";
    } else if (code === ARABIC_LETTER_MARK || code === LTR_MARK || code === RTL_MARK) {
      // Invisible, but they leak into string comparisons and snapshots. Drop.
    } else if (code === NBSP || code === NARROW_NBSP) {
      out += " ";
    } else {
      out += char;
    }
  }
  return out;
}

/**
 * Strips any `-u-nu-` (numbering system) or `-u-ca-` (calendar) extension
 * arriving from navigator.language. A phone set to `ar-SA-u-ca-islamic` would
 * otherwise drag a Hijri calendar into a display the brief requires to be
 * Gregorian.
 */
export function sanitiseLocaleTag(tag: string): string {
  return tag.replace(/-u-(nu|ca)-[a-z0-9-]+/gi, "");
}

export function resolveLocale(navigatorLanguage: string | undefined): AppLocale {
  const tag = sanitiseLocaleTag(navigatorLanguage ?? "").toLowerCase();
  return tag.startsWith("ar") ? "ar" : "en";
}

export interface NumberFormatOptions {
  readonly minimumFractionDigits?: number;
  readonly maximumFractionDigits?: number;
  readonly useGrouping?: boolean;
}

export function formatNumber(
  value: number | string,
  locale: AppLocale = "en",
  options: NumberFormatOptions = {},
): string {
  const numeric = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numeric)) return "0";

  const formatter = new Intl.NumberFormat(LOCALE_TAG[locale], {
    numberingSystem: "latn",
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
    useGrouping: options.useGrouping ?? true,
  });

  return latiniseDigits(formatter.format(numeric));
}

/**
 * Formats an already-rounded decimal STRING without routing through Number, so
 * precision beyond float range survives intact. Grouping applies to the integer
 * part only. This is what amounts actually render through — decimal.js produces
 * the string, and this only inserts separators.
 */
export function formatDecimalString(value: string, useGrouping = true): string {
  const negative = value.startsWith("-");
  const bare = negative ? value.slice(1) : value;
  const [intPart = "0", fracPart] = bare.split(".");
  const grouped = useGrouping ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : intPart;
  const joined = fracPart !== undefined ? `${grouped}.${fracPart}` : grouped;
  return negative ? `-${joined}` : joined;
}

export interface DateFormatOptions {
  readonly day?: "numeric" | "2-digit";
  readonly month?: "numeric" | "2-digit" | "short" | "long";
  readonly year?: "numeric" | "2-digit";
  readonly hour?: "numeric" | "2-digit";
  readonly minute?: "numeric" | "2-digit";
}

export function formatDate(
  value: Date | string,
  locale: AppLocale = "en",
  options: DateFormatOptions = { day: "2-digit", month: "short", year: "numeric" },
): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";

  /*
   * The calendar is applied AFTER the caller's options and cannot be
   * overridden. Written as an explicit strip-then-set rather than a second
   * spread, because spreading the same key twice is a TypeScript error and,
   * more importantly, reads as an accident to the next person.
   */
  const { calendar: _ignored, ...callerOptions } = options as DateFormatOptions & {
    calendar?: string;
  };
  void _ignored;

  const formatter = new Intl.DateTimeFormat(LOCALE_TAG[locale], {
    ...callerOptions,
    numberingSystem: "latn",
    calendar: "gregory",
    timeZone: "UTC",
  });

  return latiniseDigits(formatter.format(date));
}

/**
 * Test seam for the TAG itself.
 *
 * Necessary because asserting the resolved calendar only fails on platforms
 * whose ICU defaults `ar-SA` to islamic-umalqura. This machine's does not, so
 * a resolved-value test passed here even with the forcing removed — it would
 * have shipped the Hijri bug a second time. Asserting that the tag carries
 * `-ca-gregory` and `-nu-latn` fails everywhere, including here.
 */
export function localeTag(locale: AppLocale): string {
  return LOCALE_TAG[locale];
}

/**
 * Test seam: what the platform ACTUALLY resolved, not what we asked for.
 * Asserting the formatted string would pass on a machine whose ICU already
 * defaults to Gregorian and fail to protect the phones that do not.
 */
export function resolvedDateOptions(locale: AppLocale = "en") {
  return new Intl.DateTimeFormat(LOCALE_TAG[locale], {
    numberingSystem: "latn",
    calendar: "gregory",
    timeZone: "UTC",
  }).resolvedOptions();
}
